// ============================================================
// provenance - a session's copies and the library they came from. Pure.
//
// "Use in this session" COPIES (the user's ruling, 2026-10-18): the copy
// carries `origin: {assetId, version}` so its card can say a newer version
// is waiting, and the table never reads the library. An origin this
// account does not own - an imported session, another GM's file - is
// FOREIGN and counts as none: no "newer", and a save makes a new asset.
// ============================================================
import type { AssetRow, Origin } from './types';

/** The library row a copy came from, if this account still has it live. */
export function ownedRow(rows: readonly AssetRow[], origin: Origin | null | undefined): AssetRow | null {
  if (!origin) return null;
  return rows.find((r) => r.id === origin.assetId && !r.deletedAt) ?? null;
}

/** Is there a newer version of this copy in the library? */
export function isNewer(rows: readonly AssetRow[], origin: Origin | null | undefined): boolean {
  const row = ownedRow(rows, origin);
  return !!row && !!origin && row.version > origin.version;
}

/** The copies in a session that have a newer version in the library. */
export function staleCopies<T extends { origin?: Origin | null }>(
  copies: readonly T[],
  rows: readonly AssetRow[],
): T[] {
  return copies.filter((c) => isNewer(rows, c.origin));
}

/** An origin read off the wire: whole, or none. */
/** Where a copy of `row`'s current version comes from. */
export const originOf = (row: Pick<AssetRow, 'id' | 'version'>): Origin => ({ assetId: row.id, version: row.version });

export function readOrigin(raw: unknown): Origin | null {
  const o = raw as Partial<Origin> | null;
  if (!o || typeof o !== 'object' || typeof o.assetId !== 'string' || !o.assetId) return null;
  return { assetId: o.assetId, version: typeof o.version === 'number' && o.version > 0 ? o.version : 0 };
}

/**
 * The id a session's copy of a library character takes. The copy of this
 * asset already there keeps its id (an Update writes over it); else the
 * sheet's own id - unless another sheet in the session holds it (another
 * asset's copy, or one made at the table), when `mint` gives it one of its own.
 */
export function copyIdFor(
  assetId: string,
  sheetId: string,
  held: readonly { id: string; origin?: Origin | null }[],
  mint: () => string,
): string {
  const earlier = held.find((c) => c.origin?.assetId === assetId);
  if (earlier) return earlier.id;
  return held.some((c) => c.id === sheetId) ? mint() : sheetId;
}
